import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ActorStamp } from "@/components/shared/ActorStamp";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { PlayCircle } from "lucide-react";
import { PilotReadinessTab } from "@/components/admin/PilotReadinessTab";
import { CutoverReadinessTab } from "@/components/admin/CutoverReadinessTab";
import { AdminOverview } from "@/components/admin/AdminOverview";
import { UsersPanel } from "@/components/admin/UsersPanel";
import { PersonasTab } from "@/components/admin/PersonasTab";
import { MigrationControlPanel } from "@/components/admin/MigrationControlPanel";
import { WorkflowOptionsTab } from "@/components/admin/WorkflowOptionsTab";

function UsersTab({ initialFilter }) {
  const { memberships: myMemberships } = useAuth();
  const isAdmin = myMemberships.some((m) => m.org_type === "INTERNAL" && m.role === "JIGSAW_ADMIN");
  if (!isAdmin) return <p data-testid="admin-users-restricted" className="rounded-md border border-jigsaw-border bg-white px-4 py-3 text-sm text-jigsaw-muted">Only a Jigsaw Admin can view or manage platform accounts. Your Jigsaw Staff role covers the operational workspace; account and membership changes are made by an Admin.</p>;
  return <UsersPanel initialFilter={initialFilter} />;
}

function AdminOnly({ testId, children }) {
  const { memberships } = useAuth();
  const isAdmin = memberships.some((m) => m.org_type === "INTERNAL" && m.role === "JIGSAW_ADMIN");
  if (!isAdmin) return <p data-testid={testId} className="rounded-md border border-jigsaw-border bg-white px-4 py-3 text-sm text-jigsaw-muted">Only a Jigsaw Admin can open this view.</p>;
  return children;
}

function AuditTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get("/admin/audit").then((r) => setData(r.data)).catch(() => setError(true)); }, []);
  if (error) return <ErrorState testId="admin-audit-error" />;
  if (!data) return <LoadingState testId="admin-audit-loading" />;
  if (data.items.length === 0) return <p data-testid="admin-audit-empty" className="text-sm text-jigsaw-muted">No audit events recorded yet.</p>;
  return (
    <Table data-testid="admin-audit-table">
      <TableHeader><TableRow><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>By</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
      <TableBody>
        {data.items.map((a) => (
          <TableRow key={a.id} data-testid={`admin-audit-row-${a.id}`}><TableCell className="font-mono-ref text-xs text-jigsaw-navy">{a.action}</TableCell><TableCell className="text-xs text-jigsaw-muted">{a.entity_type}</TableCell><TableCell className="text-xs"><ActorStamp event={a} testId={`admin-audit-actor-${a.id}`} /></TableCell><TableCell className="text-xs text-jigsaw-muted">{new Date(a.created_at).toLocaleString()}</TableCell></TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function MigrationTab() {
  const [dataset, setDataset] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);

  const loadHistory = () => api.get("/admin/migration/dry-runs").then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); }, []);

  const loadExample = async () => {
    const { data } = await api.get("/admin/migration/sample-dataset");
    setDataset(JSON.stringify(data, null, 2));
  };

  const run = async () => {
    try {
      const payload = JSON.parse(dataset);
      const { data } = await api.post("/admin/migration/dry-run", payload);
      setResult(data);
      loadHistory();
      toast.success("Dry run complete - no data was written to live tables");
    } catch (e) { toast.error("Invalid JSON or dry run failed"); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-jigsaw-muted">Legacy schema mapping never merges <code>guests</code> (a stay allocation) with <code>guest_profile</code> (the person). This tool only validates - it never writes to live tables.</p>
      <div className="flex gap-2">
        <Button data-testid="migration-load-example-button" size="sm" variant="outline" onClick={loadExample}>Load example dataset</Button>
        <Button data-testid="migration-run-button" size="sm" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark" onClick={run} disabled={!dataset}><PlayCircle className="mr-1 h-3.5 w-3.5" /> Run dry run</Button>
      </div>
      <Textarea data-testid="migration-dataset-textarea" rows={10} value={dataset} onChange={(e) => setDataset(e.target.value)} className="font-mono-ref text-xs" placeholder="Paste or load a sample legacy dataset (JSON)" />
      {result && (
        <div data-testid="migration-result" className="rounded-md border border-jigsaw-border bg-jigsaw-bg p-3">
          <p className="text-sm font-medium text-jigsaw-navy">Status: {result.status}</p>
          <pre className="mt-2 overflow-x-auto text-xs text-jigsaw-muted">{JSON.stringify(result.summary, null, 2)}</pre>
        </div>
      )}
      {history && history.items.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-medium text-jigsaw-navy">Previous dry runs</p>
          <Table data-testid="migration-history-table">
            <TableHeader><TableRow><TableHead>Status</TableHead><TableHead>Started</TableHead></TableRow></TableHeader>
            <TableBody>{history.items.map((h) => <TableRow key={h.id}><TableCell>{h.status}</TableCell><TableCell className="text-xs text-jigsaw-muted">{new Date(h.started_at).toLocaleString()}</TableCell></TableRow>)}</TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function IntegrationsTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get("/integrations").then((r) => setData(r.data)).catch(() => setError(true)); }, []);
  if (error) return <ErrorState testId="admin-integrations-error" />;
  if (!data) return <LoadingState testId="admin-integrations-loading" />;
  const NAMES = { MS_GRAPH: "Microsoft 365 (mail dispatch, Copilot)", XERO: "Xero (invoice export)", AI: "Jigsaw AI drafting" };
  return (
    <div className="space-y-3">
      <p className="text-sm text-jigsaw-muted">Every provider is off by default and is switched on at deployment by the operations team; the connection steps live in the operations runbook, not in this screen. Statuses below are live.</p>
      <Table data-testid="admin-integrations-table">
        <TableHeader><TableRow><TableHead>Provider</TableHead><TableHead>Status</TableHead><TableHead>What it enables</TableHead></TableRow></TableHeader>
        <TableBody>
          {data.items.map((i) => (
            <TableRow key={i.provider} data-testid={`admin-integration-row-${i.provider}`}>
              <TableCell className="font-medium text-jigsaw-navy">{NAMES[i.provider] || i.provider}</TableCell>
              <TableCell><StatusBadge status={i.status} /></TableCell>
              <TableCell className="text-[13px] text-jigsaw-muted">{i.provider === "MS_GRAPH" ? "Queued communications leave the platform from the staff member's own mailbox." : i.provider === "XERO" ? "Recorded charges can be exported as invoices." : "PREPARE-tier draft summaries for human review (also needs the tenant opt-in on AI & Automations)."}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SlaTab() {
  const { memberships } = useAuth();
  const isAdmin = memberships.some((m) => m.org_type === "INTERNAL" && m.role === "JIGSAW_ADMIN");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [edit, setEdit] = useState({});
  const load = () => api.get("/admin/sla-policies").then((r) => { setData(r.data); setEdit(Object.fromEntries(r.data.priorities.map((p) => [p.priority, { first: p.policy?.first_response_hours ?? "", resolution: p.policy?.resolution_hours ?? "" }]))); }).catch(() => setError(true));
  useEffect(() => { load(); }, []);
  if (error) return <ErrorState testId="admin-sla-error" />;
  if (!data) return <LoadingState testId="admin-sla-loading" />;
  const save = async (priority) => {
    const v = edit[priority];
    try {
      await api.put(`/admin/sla-policies/${priority}`, { priority, first_response_hours: v.first === "" ? null : Number(v.first), resolution_hours: v.resolution === "" ? null : Number(v.resolution), is_active: true });
      toast.success(`${priority} response targets saved (audited)`); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not save targets"); }
  };
  const clear = async (priority) => { try { await api.delete(`/admin/sla-policies/${priority}`); toast.success(`${priority} returned to Not configured`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not clear"); } };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-jigsaw-muted">Response targets per enquiry priority in elapsed UTC hours from the enquiry being raised (no working-hours calendar). Deadlines are snapshotted onto each enquiry when it is raised, so later edits never rewrite history. First response counts only when staff record it with evidence; resolution when the enquiry completes (cancellation is recorded as cancelled, with any breach retained). Nothing is assumed until the business policy is entered here.</p>
        <StatusBadge status={data.configured ? "ACTIVE" : "NOT_CONFIGURED"} />
      </div>
      <Table data-testid="admin-sla-table">
        <TableHeader><TableRow><TableHead>Priority</TableHead><TableHead>First response (h)</TableHead><TableHead>Resolution (h)</TableHead><TableHead>State</TableHead><TableHead>Last changed</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {data.priorities.map((p) => (
            <TableRow key={p.priority} data-testid={`admin-sla-row-${p.priority}`}>
              <TableCell><StatusBadge status={p.priority} /></TableCell>
              <TableCell><Input data-testid={`sla-first-${p.priority}`} type="number" min="1" className="h-8 w-24" disabled={!isAdmin} value={edit[p.priority]?.first ?? ""} onChange={(e) => setEdit({ ...edit, [p.priority]: { ...edit[p.priority], first: e.target.value } })} /></TableCell>
              <TableCell><Input data-testid={`sla-resolution-${p.priority}`} type="number" min="1" className="h-8 w-24" disabled={!isAdmin} value={edit[p.priority]?.resolution ?? ""} onChange={(e) => setEdit({ ...edit, [p.priority]: { ...edit[p.priority], resolution: e.target.value } })} /></TableCell>
              <TableCell><StatusBadge status={p.policy && p.policy.is_active && (p.policy.first_response_hours || p.policy.resolution_hours) ? "ACTIVE" : "NOT_CONFIGURED"} /></TableCell>
              <TableCell className="text-[12px] text-jigsaw-muted">{p.policy ? new Date(p.policy.updated_at).toLocaleString("en-GB") : "—"}</TableCell>
              <TableCell className="text-right">{isAdmin && <span className="flex justify-end gap-1"><Button data-testid={`sla-save-${p.priority}`} size="sm" variant="outline" onClick={() => save(p.priority)} disabled={edit[p.priority]?.first === "" && edit[p.priority]?.resolution === ""}>Save</Button>{p.policy && <Button data-testid={`sla-clear-${p.priority}`} size="sm" variant="ghost" className="text-jigsaw-danger" onClick={() => clear(p.priority)}>Clear</Button>}</span>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!isAdmin && <p className="text-xs text-jigsaw-muted">Only a Jigsaw Admin can change response targets.</p>}
    </div>
  );
}

export default function Admin() {
  const [params] = useSearchParams();
  const TABS = ["overview", "users", "personas", "audit", "migration", "promotion", "integrations", "sla", "workflow", "pilot", "cutover"];
  const [tab, setTab] = useState(TABS.includes(params.get("tab")) ? params.get("tab") : "overview");
  const [userFilter, setUserFilter] = useState(null);
  return (
    <div data-testid="admin-page" className="space-y-5">
      <div>
        <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">Admin Centre</h1>
        <p className="text-sm text-jigsaw-muted">Accounts, roles and sessions · persona capabilities · audit trail · migration dry-run · integrations · response targets · Work Flow labels · pilot readiness.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="admin-tabs" className="h-auto flex-wrap">
          <TabsTrigger value="overview" data-testid="admin-tab-overview" className="whitespace-normal">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="whitespace-normal">Accounts</TabsTrigger>
          <TabsTrigger value="personas" data-testid="admin-tab-personas" className="whitespace-normal">Personas</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit" className="whitespace-normal">Audit Trail</TabsTrigger>
          <TabsTrigger value="migration" className="whitespace-normal">Migration Dry-Run</TabsTrigger>
          <TabsTrigger value="promotion" data-testid="admin-tab-promotion" className="whitespace-normal">Legacy promotion</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="admin-tab-integrations" className="whitespace-normal">Integrations</TabsTrigger>
          <TabsTrigger value="sla" data-testid="admin-tab-sla" className="whitespace-normal">Response targets</TabsTrigger>
          <TabsTrigger value="workflow" data-testid="admin-tab-workflow" className="whitespace-normal">Work Flow labels</TabsTrigger>
          <TabsTrigger value="pilot" data-testid="admin-tab-pilot" className="whitespace-normal">Pilot readiness</TabsTrigger>
          <TabsTrigger value="cutover" data-testid="admin-tab-cutover" className="whitespace-normal">Cutover readiness</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><AdminOnly testId="admin-overview-restricted"><AdminOverview onGoToUsers={(f) => { setUserFilter(f); setTab("users"); }} /></AdminOnly></TabsContent>
        <TabsContent value="users"><UsersTab initialFilter={userFilter} /></TabsContent>
        <TabsContent value="promotion"><AdminOnly testId="admin-promotion-restricted"><MigrationControlPanel /></AdminOnly></TabsContent>
        <TabsContent value="personas"><AdminOnly testId="admin-personas-restricted"><PersonasTab /></AdminOnly></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="migration"><MigrationTab /></TabsContent>
        <TabsContent value="integrations"><IntegrationsTab /></TabsContent>
        <TabsContent value="sla"><SlaTab /></TabsContent>
        <TabsContent value="workflow"><WorkflowOptionsTab /></TabsContent>
        <TabsContent value="pilot"><PilotReadinessTab /></TabsContent>
        <TabsContent value="cutover"><CutoverReadinessTab /></TabsContent>
      </Tabs>
    </div>
  );
}
